import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { sendContactForm, validateContactForm, ContactFormData } from '../services/mailgunService';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Validate form data
      const validation = validateContactForm(formData as ContactFormData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Send contact form via Mailgun
      const emailResult = await sendContactForm(formData as ContactFormData);
      
      if (!emailResult.success) {
        throw new Error(emailResult.message || 'Failed to send contact form');
      }

      console.log('Contact form sent successfully:', emailResult);
      
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          phone: '',
          service: '',
          message: '',
        });
      }, 3000);
      
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setIsSubmitting(false);
      
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to submit form. Please try again or contact us directly at contact@booplogics.com');
      }
    }
  };

  const contactInfo = [
    {
      icon: EnvelopeIcon,
      title: 'Email',
      details: ['contact@booplogics.com'],
      description: 'We typically respond within 24 hours',
    },
    {
      icon: PhoneIcon,
      title: 'Phone',
      details: ['919-370-8871'],
      description: 'Mon-Fri 9AM-6PM EST',
    },
    {
      icon: MapPinIcon,
      title: 'Office',
      details: ['Raleigh, NC'],
      description: 'Your local AI partner in RTP',
    },
  ];

  const services = [
    'Data',
    'Data & Analytics',
    'AI Products & Services',
    'AI Strategy Consulting',
    'Generative AI',
    'AI Agents',
    'AI Chatbots',
    'Other',
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 section-padding">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-6">
              Get in Touch
            </h1>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto mb-8">
              Ready to transform your business with AI? Let's discuss your needs and explore 
              how we can drive innovation and growth for your organization.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {isSubmitted ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="text-green-600 text-lg font-semibold mb-2">
                    Thank you for your message!
                  </div>
                  <p className="text-green-700">
                    Your inquiry has been saved and we'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-secondary-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-secondary-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-secondary-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-secondary-700 mb-2">
                      Company *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-secondary-700 mb-2">
                      Service of Interest
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">&nbsp;</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-secondary-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Tell us about your project, goals, and how we can help..."
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-2xl font-bold text-secondary-900 mb-6">
                Contact Information
              </h2>
              <div className="space-y-8">
                {contactInfo.map((info, index) => (
                  <div key={info.title} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-md font-semibold text-secondary-900 mb-2">
                        {info.title}
                      </h3>
                      {info.details.map((detail, detailIndex) => (
                        <p key={detailIndex} className="text-secondary-600 mb-1">
                          {detail}
                        </p>
                      ))}
                      <p className="text-sm text-secondary-500">
                        {info.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Office Hours */}
              <div className="mt-12 p-6 bg-secondary-50 rounded-lg">
                <div className="flex items-center space-x-3 mb-4">
                  <ClockIcon className="w-6 h-6 text-primary-600" />
                  <h3 className="text-md font-semibold text-secondary-900">
                    Office Hours
                  </h3>
                </div>
                <div className="space-y-2 text-secondary-600">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span>9:00 AM - 6:00 PM EST</span>
                  </div>
                                  </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    
      {/* FAQ Section */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-secondary-900 mb-4">
              Frequently Asked Questions
            </h2>
            
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-md font-semibold text-secondary-900 mb-2">
                  How quickly can you start a project?
                </h3>
                <p className="text-secondary-600 text-md">
                  We can typically begin most projects within 1-2 weeks of initial consultation, 
                  depending on project scope and complexity.
                </p>
              </div>
              <div>
                <h3 className="text-md font-semibold text-secondary-900 mb-2">
                  Do you work with small businesses?
                </h3>
                <p className="text-secondary-600 text-md">
                  Yes, our vision is to empower small to mid-size businesses to scale with AI.
                </p>
              </div>
              <div>
                <h3 className="text-md font-semibold text-secondary-900 mb-2">
                  What industries do you specialize in?
                </h3>
                <p className="text-secondary-600 text-md">
                  We have experience across healthcare, finance, retail, manufacturing, and technology sectors.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-md font-semibold text-secondary-900 mb-2">
                  Do you provide ongoing support?
                </h3>
                <p className="text-secondary-600 text-md">
                  Yes, we offer comprehensive support and maintenance for all our AI solutions.
                </p>
              </div>
              <div>
                <h3 className="text-md font-semibold text-secondary-900 mb-2">
                  How do you ensure data security?
                </h3>
                <p className="text-secondary-600 text-md">
                  We follow industry best practices and comply with all major security standards and regulations like HIPAA and SOC 2.
                </p>
              </div>
              <div>
                <h3 className="text-md font-semibold text-secondary-900 mb-2">
                  Can you help with existing AI systems?
                </h3>
                <p className="text-secondary-600 text-md">
                  Yes, we can audit, optimize, and enhance existing AI systems and infrastructure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;

